/**
 * How much of the map view a forecast model's domain covers (#327).
 *
 * "Covers 34% of this view" is a statement about the screen, so the fraction
 * is measured in Web Mercator screen area, not in lon/lat degrees: Mercator
 * stretches high latitudes (about ×2.5 at 66°N against 34°N). Vertices are
 * projected with x = lon, y = ln tan(π/4 + φ/2) (φ clamped to Mercator's
 * ±85.0511), and each ring is clipped to the view rectangle with
 * Sutherland–Hodgman, which is exact for any simple polygon, convex or not,
 * against a rectangle. Edges are straight in Mercator rather than in lon/lat;
 * at the backend's ≥0.2° simplification the difference is negligible.
 *
 * Pure and dependency-free (core is consumed as raw TS by Metro and Vite):
 * domains hold at most 100 points and the catalog about 15 models, so a full
 * pass costs well under a millisecond per `moveend`.
 */
import type {ForecastMapModelDomain} from '../../api/types';
import type {MapBounds} from './mapModes';

// Web Mercator's latitude limit.
export const MERCATOR_MAX_LATITUDE = 85.0511;

export const clampLatitude = (latitude: number): number =>
  Math.min(MERCATOR_MAX_LATITUDE, Math.max(-MERCATOR_MAX_LATITUDE, latitude));

// Floating-point residue, not area: a ring lying along the view's edge
// leaves ~1e-16 in the shoelace sum. A billionth of the view is far below a
// pixel at any zoom.
const NEGLIGIBLE_COVERAGE = 1e-9;

const mercatorY = (latitude: number): number =>
  Math.log(Math.tan(Math.PI / 4 + (clampLatitude(latitude) * Math.PI) / 360));

type Point = [number, number];
type Rect = {xMin: number; xMax: number; yMin: number; yMax: number};

// Sutherland–Hodgman against one edge of the rectangle. The crossing point
// takes the edge's coordinate exactly, so a ring that only touches the view
// collapses onto the edge and contributes exactly zero area.
const clipEdge = (
  ring: Point[],
  axis: 0 | 1,
  bound: number,
  keepAbove: boolean,
): Point[] => {
  const inside = (point: Point) =>
    keepAbove ? point[axis] >= bound : point[axis] <= bound;
  const crossing = (from: Point, to: Point): Point => {
    const t = (bound - from[axis]) / (to[axis] - from[axis]);
    const other = from[1 - axis] + t * (to[1 - axis] - from[1 - axis]);
    return axis === 0 ? [bound, other] : [other, bound];
  };
  const out: Point[] = [];
  ring.forEach((current, index) => {
    const previous = ring[(index + ring.length - 1) % ring.length];
    if (inside(current)) {
      if (!inside(previous)) {
        out.push(crossing(previous, current));
      }
      out.push(current);
    } else if (inside(previous)) {
      out.push(crossing(previous, current));
    }
  });
  return out;
};

// Shoelace in trapezoid form: an edge along a constant x adds exactly 0.
const ringArea = (ring: Point[]): number => {
  let twice = 0;
  ring.forEach((point, index) => {
    const next = ring[(index + 1) % ring.length];
    twice += (point[0] - next[0]) * (point[1] + next[1]);
  });
  return Math.abs(twice) / 2;
};

const clippedRingArea = (
  ring: readonly number[][],
  shift: number,
  rect: Rect,
): number => {
  let points: Point[] = ring.map(([lon, lat]) => [lon + shift, mercatorY(lat)]);
  points = clipEdge(points, 0, rect.xMin, true);
  points = clipEdge(points, 0, rect.xMax, false);
  points = clipEdge(points, 1, rect.yMin, true);
  points = clipEdge(points, 1, rect.yMax, false);
  return points.length < 3 ? 0 : ringArea(points);
};

/** Every polygon of the domain, as `[exterior, ...holes]`. */
const domainPolygons = (domain: ForecastMapModelDomain): number[][][][] =>
  domain.type === 'Polygon' ? [domain.coordinates] : domain.coordinates;

/**
 * The fraction (0..1) of the view's screen area that the domain covers.
 * `null` is a global model: 1. A degenerate view (zero or non-finite size)
 * reads 0. Holes subtract, parts add.
 *
 * The view may be a world copy (maplibre reports longitudes beyond ±180) or
 * cross the antimeridian either way (west > east). Its west edge is folded
 * into [-180, 180) and its width capped at 360°; each domain part (longitudes
 * in [-180, 180]) is then measured as is and shifted by +360°, which together
 * cover the folded view exactly once.
 *
 * A view that only touches the domain reads exactly 0 (the camera rule
 * depends on it): a west edge already in range is never folded, since the
 * modulo arithmetic can move it by an ulp past the domain's edge, and
 * shoelace residue below `NEGLIGIBLE_COVERAGE` is dropped.
 */
export const viewCoverage = (
  view: Readonly<MapBounds>,
  domain: ForecastMapModelDomain | null,
): number => {
  if (domain === null) {
    return 1;
  }
  const [west, south, east, north] = view;
  if (!view.every(Number.isFinite)) {
    return 0;
  }
  const width = Math.min(360, east >= west ? east - west : east + 360 - west);
  const xMin =
    west >= -180 && west < 180
      ? west
      : ((((west + 180) % 360) + 360) % 360) - 180;
  const rect: Rect = {
    xMin,
    xMax: xMin + width,
    yMin: mercatorY(south),
    yMax: mercatorY(north),
  };
  if (!(width > 0) || !(rect.yMax > rect.yMin)) {
    return 0;
  }
  const viewArea = width * (rect.yMax - rect.yMin);
  let covered = 0;
  for (const [exterior = [], ...holes] of domainPolygons(domain)) {
    for (const shift of [0, 360]) {
      covered += clippedRingArea(exterior, shift, rect);
      for (const hole of holes) {
        covered -= clippedRingArea(hole, shift, rect);
      }
    }
  }
  const fraction = covered / viewArea;
  return fraction < NEGLIGIBLE_COVERAGE ? 0 : Math.min(1, fraction);
};

export type CoverageClass =
  | {kind: 'full'}
  | {kind: 'partial'; fraction: number}
  | {kind: 'none'};

const FULL_COVERAGE = 0.995;
const NO_COVERAGE = 0.005;

/** Full from 99.5%, none under 0.5%, partial (with the fraction) between. */
export const classifyCoverage = (fraction: number): CoverageClass => {
  if (fraction >= FULL_COVERAGE) {
    return {kind: 'full'};
  }
  if (fraction < NO_COVERAGE) {
    return {kind: 'none'};
  }
  return {kind: 'partial', fraction};
};

const DEGREES = Math.PI / 180;

/*
∬cos(λ) dA and ∬sin(λ) dA over a ring in (λ radians, Mercator y), by Green's
theorem — ∬f(λ) dA = ∮F(λ) dy with F' = f — edge by edge, y linear in λ
along an edge. Signed by the ring's winding, like its area.
*/
const ringMoments = (ring: readonly number[][]): [number, number, number] => {
  let area = 0;
  let cos = 0;
  let sin = 0;
  ring.forEach(([lon0, lat0], index) => {
    const [lon1, lat1] = ring[(index + 1) % ring.length];
    const [x0, x1] = [lon0 * DEGREES, lon1 * DEGREES];
    const dy = mercatorY(lat1) - mercatorY(lat0);
    area += ((x0 + x1) / 2) * dy;
    if (x1 === x0) {
      cos += Math.sin(x0) * dy;
      sin -= Math.cos(x0) * dy;
    } else {
      const slope = dy / (x1 - x0);
      cos += slope * (Math.cos(x0) - Math.cos(x1));
      sin -= slope * (Math.sin(x1) - Math.sin(x0));
    }
  });
  return [area, cos, sin];
};

/**
 * The circular mean longitude of a domain, weighted by Mercator area: the
 * direction of ∬(cos λ, sin λ) dA. Unlike a plain mean it does not care where
 * the seam is — mass either side of ±180 averages to 180, not 0. Holes
 * subtract; parts add; ring winding does not matter.
 */
const meanLongitude = (domain: ForecastMapModelDomain): number => {
  let cos = 0;
  let sin = 0;
  for (const rings of domainPolygons(domain)) {
    rings.forEach((ring, index) => {
      const [area, ringCos, ringSin] = ringMoments(ring);
      const sign = Math.sign(area) * (index === 0 ? 1 : -1);
      cos += sign * ringCos;
      sin += sign * ringSin;
    });
  }
  return Math.atan2(sin, cos) / DEGREES;
};

/**
 * The bounds to fit to show a whole domain, latitudes clamped to Mercator.
 *
 * Longitudes go round the circle: the fit spans everything except the widest
 * stretch of longitude the domain leaves empty. When that stretch is not the
 * one across ±180, the fit crosses the antimeridian and west > east, which
 * maplibre's `fitBounds` accepts. A domain with no gap at all (a polar cap
 * served as -180..180, e.g. EC_RDPS) is centred on its mass instead of on
 * 0°: `[c - 180, s, c + 180, n]`, `c` being the circular mean longitude over
 * the domain's Mercator area. A domain with no coordinates fits the whole
 * world.
 */
export const domainFitBounds = (domain: ForecastMapModelDomain): MapBounds => {
  const exteriors = domainPolygons(domain)
    .map(([exterior = []]) => exterior)
    .filter((exterior) => exterior.length > 0);
  if (exteriors.length === 0) {
    return [-180, -MERCATOR_MAX_LATITUDE, 180, MERCATOR_MAX_LATITUDE];
  }
  const latitudes = exteriors.flatMap((exterior) => exterior.map((p) => p[1]));
  const spans = exteriors
    .map((exterior): [number, number] => {
      const longitudes = exterior.map((p) => p[0]);
      return [Math.min(...longitudes), Math.max(...longitudes)];
    })
    .sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [from, to] of spans) {
    const last = merged[merged.length - 1];
    if (last !== undefined && from <= last[1]) {
      last[1] = Math.max(last[1], to);
    } else {
      merged.push([from, to]);
    }
  }
  // The gap across ±180 first: on a tie the fit stays a plain box.
  let west = merged[0][0];
  let east = merged[merged.length - 1][1];
  let widestGap = west + 360 - east;
  for (let index = 1; index < merged.length; index++) {
    const gap = merged[index][0] - merged[index - 1][1];
    if (gap > widestGap) {
      widestGap = gap;
      west = merged[index][0];
      east = merged[index - 1][1];
    }
  }
  const south = clampLatitude(Math.min(...latitudes));
  const north = clampLatitude(Math.max(...latitudes));
  if (widestGap <= 0) {
    const centre = meanLongitude(domain);
    return [centre - 180, south, centre + 180, north];
  }
  return [west, south, east, north];
};
